/**
 * Runtime-config schema shared between Worker and Frontend.
 *
 * Unlike the CLI tool, the Worker doesn't load a config.jsonc. Instead:
 *   - chain RPC URLs come from wrangler secrets / vars
 *   - policy constants (slippage, dwell bounds, amount bounds, skim rate)
 *     come from `PolicyConfig` below, bundled at build time.
 *
 * SOURCE-OF-TRUTH: `DEFAULT_POLICY` is the single authority for:
 *   - `amounts.min_usdt` / `amounts.max_usdt`   (ported from CLI wizard)
 *   - `dwell_time.min_seconds` / `max_seconds`  (ported from CLI wizard)
 *   - `treasury.skim_rate`, slippage bounds, etc.
 *
 * Both the Worker (POST /api/jobs) and the Frontend (NewJob wizard)
 * import these — do NOT hardcode duplicate limits. Bumping defaults here
 * means redeploying the Worker; policy changes require a deploy artifact
 * for audit trails.
 */

import { z } from "zod";

export const PolicyConfigSchema = z.object({
  network: z.enum(["mainnet", "testnet"]).default("mainnet"),
  swap: z.object({
    max_slippage_per_hop: z.number().min(0).max(0.2).default(0.01),
    max_total_slippage: z.number().min(0).max(0.5).default(0.03),
  }),
  dwell_time: z
    .object({
      min_seconds: z.number().int().positive(),
      max_seconds: z.number().int().positive(),
    })
    .refine((d) => d.max_seconds >= d.min_seconds, {
      message: "max_seconds must be >= min_seconds",
      path: ["max_seconds"],
    }),
  amounts: z
    .object({
      min_usdt: z.number().positive().default(2000),
      max_usdt: z.number().positive().default(30000),
    })
    .refine((a) => a.max_usdt >= a.min_usdt, {
      path: ["max_usdt"],
      message: "max_usdt must be >= min_usdt",
    }),
  thorchain: z.object({
    inbound_confirmation_timeout_seconds: z.number().int().positive().default(600),
    outbound_confirmation_timeout_seconds: z.number().int().positive().default(600),
    required_trx_for_fuel: z.number().positive().default(35),
    wallet_b_eth_fuel: z.number().positive().default(0.04),
  }),
  railgun: z.object({
    poi_enabled: z.boolean().default(true),
    broadcaster_max_fee_bps: z.number().int().min(0).max(5000).default(1500),
  }),
  treasury: z.object({
    skim_rate: z.number().min(0).max(0.2).default(0.05),
    min_trx_reserve: z.number().positive().default(100),
    target_trx_reserve: z.number().positive().default(300),
  }),
});

export type PolicyConfig = z.infer<typeof PolicyConfigSchema>;

/**
 * Defaults baked into the Worker image. Override via env vars if the Worker
 * is ever deployed into a different profile (e.g. testnet).
 */
export const DEFAULT_POLICY: PolicyConfig = PolicyConfigSchema.parse({
  network: "mainnet",
  swap: { max_slippage_per_hop: 0.01, max_total_slippage: 0.03 },
  dwell_time: { min_seconds: 7 * 86_400, max_seconds: 21 * 86_400 },
  amounts: { min_usdt: 2000, max_usdt: 30_000 },
  thorchain: {
    inbound_confirmation_timeout_seconds: 600,
    outbound_confirmation_timeout_seconds: 600,
    required_trx_for_fuel: 35,
    wallet_b_eth_fuel: 0.04,
  },
  railgun: { poi_enabled: true, broadcaster_max_fee_bps: 1500 },
  treasury: { skim_rate: 0.05, min_trx_reserve: 100, target_trx_reserve: 300 },
});
